import type { DocArticle } from './documents.ts';

/**
 * The two access steps a client runs in PowerShell because consent cannot
 * grant them: Exchange roles and a Purview role group. Kept apart from the rest
 * of "Connecting clients" because every command here is typed into a customer's
 * tenant exactly as printed.
 */

/*
 * The commands are the ones in docs/user-guide/exchange-access-not-permitted.md,
 * which records them as run against a live tenant. Change them there first.
 */
const DRIFTWATCH_APP_ID = '03d778d8-317d-426f-b89b-4cac6699c4f9';

export const GRANTING_EXCHANGE_ACCESS: DocArticle = {
  id: 'granting-exchange-access',
  title: 'Granting Exchange access',
  summary: 'The exact PowerShell commands a client runs to give DriftWatch its three view-only Exchange roles.',
  sections: [
    {
      id: 'exchange-before',
      heading: 'Before you start',
      paragraphs: [
        'This is run by the client, in the client’s own tenant, by an Exchange Administrator or Global Administrator, after admin consent has been granted. It cannot be done in the Exchange admin centre: its role pages assign roles to people and groups, not to applications.',
        'It needs PowerShell 7 (7.6 or later) and the ExchangeOnlineManagement module, 3.10.1 or later. Install the module once, for the administrator’s own account, and answer A when asked whether to trust PSGallery:',
      ],
      code: ['Install-Module ExchangeOnlineManagement -Scope CurrentUser'],
      bullets: [
        `DriftWatch’s Application (client) ID is the same in every tenant: ${DRIFTWATCH_APP_ID}.`,
        'The Object ID is different in every tenant. It is the Object ID of DriftWatch’s enterprise application, found in the client’s Entra admin centre under Enterprise applications → DriftWatch → Overview. Never the Object ID of an app registration.',
        'Paste one command at a time, and copy them from this page rather than from a forwarded email: mail and chat tools strip characters and curl quotes.',
      ],
    },
    {
      id: 'exchange-sign-in',
      heading: 'Sign in to the client’s Exchange Online',
      paragraphs: [
        'In a new PowerShell 7 window, replacing admin@contoso.com with an administrator account in the client tenant, then confirm where you are before changing anything:',
      ],
      code: [
        'Connect-ExchangeOnline -UserPrincipalName admin@contoso.com',
        'Get-ConnectionInformation | Format-List Name, UserPrincipalName, TenantID, IsEopSession',
      ],
      bullets: [
        'TenantID must be the client’s tenant ID.',
        'IsEopSession must be False. True means a Security & Compliance session, where these commands do not exist: disconnect and use Connect-ExchangeOnline instead.',
      ],
    },
    {
      id: 'exchange-object-id',
      heading: 'Put the Object ID in a variable',
      paragraphs: [
        'Replace <OBJECT-ID>, angle brackets included, with the enterprise application’s Object ID, keeping the single quotes. Nothing is printed. Run everything below in the same window.',
      ],
      code: ["$SpObjectId = '<OBJECT-ID>'"],
    },
    {
      id: 'exchange-register',
      heading: 'Register DriftWatch with Exchange Online',
      paragraphs: [
        'This creates a pointer in Exchange to the enterprise application that already exists in Entra. It grants nothing on its own, and prints one row with DisplayName, ObjectId and AppId.',
      ],
      code: [
        `New-ServicePrincipal -AppId ${DRIFTWATCH_APP_ID} -ObjectId $SpObjectId -DisplayName "DriftWatch (read-only)"`,
      ],
      bullets: [
        'If it fails saying the ExternalDirectoryObjectId is already used, DriftWatch was registered earlier — by a previous attempt or by the Purview step. That is fine: check the registration below prints one row whose ObjectId is the client’s Object ID, and carry on.',
        'Any other failure is almost always the wrong Object ID: an app registration’s, or one from a different tenant.',
      ],
    },
    {
      id: 'exchange-check-registration',
      heading: 'If DriftWatch was already registered',
      paragraphs: ['Only needed when the previous command reported the registration already exists:'],
      code: [`Get-ServicePrincipal | Where-Object AppId -eq '${DRIFTWATCH_APP_ID}'`],
    },
    {
      id: 'exchange-assign-roles',
      heading: 'Assign the three view-only roles',
      paragraphs: [
        'Run these one at a time. Each prints one row naming the new assignment and the role. If one fails because the assignment already exists, that role is already in place.',
      ],
      code: [
        'New-ManagementRoleAssignment -App $SpObjectId -Role "View-Only Configuration"',
        'New-ManagementRoleAssignment -App $SpObjectId -Role "View-Only Recipients"',
        'New-ManagementRoleAssignment -App $SpObjectId -Role "View-Only Audit Logs"',
      ],
    },
    {
      id: 'exchange-verify',
      heading: 'Check the result',
      paragraphs: [
        'This is the question DriftWatch asks Exchange at the start of every assessment. It must list exactly these three roles and nothing else, each with Enabled True:',
      ],
      code: [
        `Get-ManagementRoleAssignment -RoleAssignee ${DRIFTWATCH_APP_ID} | Format-Table Name, Role, Enabled`,
      ],
      bullets: [
        'View-Only Configuration',
        'View-Only Recipients',
        'View-Only Audit Logs',
        'If PowerShell says the assignee could not be found, run the same command with $SpObjectId in place of the Application ID.',
        'If any other role is listed, DriftWatch will read nothing from Exchange until it is removed: see Removing access below, and remove only that assignment by its Name.',
      ],
    },
    {
      id: 'exchange-disconnect',
      heading: 'Disconnect and assess again',
      paragraphs: [
        'Disconnect rather than only closing the window; abandoned sessions count against a Microsoft limit until they expire. Then run an assessment from the client’s Overview tab. New assignments usually apply within minutes, and Microsoft documents up to two hours. If the Exchange controls still say not permitted, wait and run it again rather than changing the roles.',
      ],
      code: ['Disconnect-ExchangeOnline -Confirm:$false'],
    },
    {
      id: 'exchange-do-not',
      heading: 'What not to do',
      paragraphs: ['Each of these makes DriftWatch refuse to read Exchange, rather than read more of it.'],
      bullets: [
        'Do not assign any Exchange role beyond the three above.',
        'Do not give the application an Entra directory role such as Exchange Administrator or Global Reader. The one directory role it may hold is Teams Reader.',
        'Do not run any of this in the tenant that hosts DriftWatch. The roles belong to each client tenant separately.',
      ],
    },
    {
      id: 'exchange-remove',
      heading: 'Removing access',
      paragraphs: [
        'To remove one wrong assignment, list the assignments, copy the Name of the one to remove (not the role), and remove it, answering Y:',
      ],
      code: [
        `Get-ManagementRoleAssignment -RoleAssignee ${DRIFTWATCH_APP_ID} | Format-Table Name, Role`,
        'Remove-ManagementRoleAssignment -Identity "<ASSIGNMENT-NAME>"',
        `Get-ManagementRoleAssignment -RoleAssignee ${DRIFTWATCH_APP_ID} | Remove-ManagementRoleAssignment`,
        `Remove-ServicePrincipal -Identity ${DRIFTWATCH_APP_ID}`,
      ],
      bullets: [
        'The third command withdraws all of DriftWatch’s Exchange access, and the fourth then removes its registration. Answer Y, or A for all, when asked.',
        'The registration is shared with the Purview role group: remove DriftWatch from that group first if Purview access is being withdrawn too.',
        'The Exchange controls then report as Not evidenced again, which is the correct result.',
      ],
    },
  ],
};

/*
 * The commands are the ones in docs/user-guide/what-a-scan-needs.md, section 4,
 * and purview-access-not-permitted.md, which record them as run against a live
 * tenant. The six commands listed as not reads are the allow-list in
 * packages/pwsh/src/complianceGate.ts (ADR-0071). Change them there first.
 */
const PURVIEW_GROUP = 'DriftWatch Readers';

export const GRANTING_PURVIEW_ACCESS: DocArticle = {
  id: 'granting-purview-access',
  title: 'Granting Purview access',
  summary:
    'The exact PowerShell commands a client runs to put DriftWatch in a Purview role group holding three view-only roles.',
  sections: [
    {
      id: 'purview-why',
      heading: 'Why Purview needs its own step',
      paragraphs: [
        'Three controls read Microsoft Purview: DP-024 (data loss prevention for email and files), DP-025 (data loss prevention for Teams) and DP-026 (sensitivity labels published to every user). Purview ignores the Graph consent and does not accept the Exchange roles either. It decides what an application may read through its own role groups, so until this step is done those three controls report as Not evidenced, never as failures, and nothing else is affected.',
        'DriftWatch asks to be the only member of one role group, created for it, holding exactly three roles:',
      ],
      bullets: [
        'View-Only DLP Compliance Management — data loss prevention policies and rules.',
        'Sensitivity Label Reader — sensitivity label policies, and who the labels are published to.',
        'View-Only Retention Management — nothing today. Retention is not assessed; the role is part of the agreed grant so that it does not have to be changed later.',
      ],
    },
    {
      id: 'purview-before',
      heading: 'Before you start',
      paragraphs: [
        'This is run by the client, in the client’s own tenant, by a Global Administrator or a member of the Organization Management role group in Purview, after admin consent has been granted. Allow about ten minutes, plus up to an hour for Microsoft to apply the change.',
        'The Purview portal cannot do this step: its role group pages add people and groups, not applications, so there is no way to choose DriftWatch there. It is PowerShell only, with PowerShell 7 and the same ExchangeOnlineManagement module the Exchange step uses:',
      ],
      code: ['Install-Module ExchangeOnlineManagement -Scope CurrentUser'],
      bullets: [
        `DriftWatch’s Application (client) ID is the same in every tenant: ${DRIFTWATCH_APP_ID}.`,
        'The Object ID is the same value the Exchange step uses: the Object ID of DriftWatch’s enterprise application in the client tenant, from the Entra admin centre under Enterprise applications → DriftWatch → Overview.',
        `The role group is called ${PURVIEW_GROUP} here. Another name works, as long as every command uses the same one.`,
        'On a Mac, Microsoft documents Connect-IPPSSession as unavailable in PowerShell 7. It worked from a Mac with a current module on 21 September 2026; if it fails, run this procedure from Windows instead.',
      ],
    },
    {
      id: 'purview-connect',
      heading: 'Connect to Security & Compliance, not Exchange',
      paragraphs: [
        'Open a new PowerShell 7 window — not the one used for the Exchange step — and connect with an administrator account in the client tenant. Then check which service the window is talking to:',
      ],
      code: [
        'Connect-IPPSSession -UserPrincipalName admin@contoso.com',
        'Get-ConnectionInformation | Format-Table Name, ConnectionUri, IsEopSession',
      ],
      bullets: [
        'There must be exactly one row, named ExchangeOnlineProtection_1, with IsEopSession True and a ConnectionUri ending ps.compliance.protection.outlook.com.',
        'A row with IsEopSession False, or a ConnectionUri of https://outlook.office365.com, means the window is also connected to Exchange Online. Close it and start again in a fresh window.',
        'This matters because the role group commands exist in both sessions. In an Exchange session they fail with “management role can’t be found” or “couldn’t be found on …”, which reads as a missing role when it is only the wrong window.',
      ],
    },
    {
      id: 'purview-object-id',
      heading: 'Put the Object ID in a variable',
      paragraphs: [
        'Replace <OBJECT-ID>, angle brackets included, with the enterprise application’s Object ID, keeping the single quotes. Nothing is printed. Run everything below in the same window.',
      ],
      code: ["$SpObjectId = '<OBJECT-ID>'"],
    },
    {
      id: 'purview-register',
      heading: 'Register DriftWatch in Security & Compliance',
      paragraphs: [
        'Security & Compliance keeps its own list of applications, separate from Exchange’s, so DriftWatch may need registering here even though the Exchange step already did it there. Check first:',
      ],
      code: [
        'Get-ServicePrincipal -Identity $SpObjectId -ErrorAction SilentlyContinue',
        `New-ServicePrincipal -AppId ${DRIFTWATCH_APP_ID} -ObjectId $SpObjectId -DisplayName "DriftWatch (read-only)"`,
      ],
      bullets: [
        'If the first command prints a row showing the Object ID and the Application ID, DriftWatch is already registered: skip the second command.',
        'If it prints nothing, run the second. It prints one row with DisplayName, ObjectId and AppId.',
        'If the second fails saying the ExternalDirectoryObjectId is already used, check the connection again first: that error has only been seen in an Exchange window. In a Security & Compliance window it is harmless; confirm with the first command and carry on.',
      ],
    },
    {
      id: 'purview-create-group',
      heading: 'Create the role group',
      paragraphs: [
        'One command, keeping -DisplayName. It prints the group, with its three roles written as long paths ending in the role names.',
      ],
      code: [
        `New-RoleGroup -Name "${PURVIEW_GROUP}" -DisplayName "${PURVIEW_GROUP}" -Roles "View-Only DLP Compliance Management","Sensitivity Label Reader","View-Only Retention Management"`,
      ],
      bullets: [
        'Without -DisplayName the group is created, but adding DriftWatch to it then fails with “DisplayName can’t be empty”. If that has already happened, do not create a second group: give the existing one a display name with the command below, which prints nothing when it works.',
        'If it says a management role “can’t be found”, the window is connected to Exchange. Go back to the connection check.',
      ],
    },
    {
      id: 'purview-fix-display-name',
      heading: 'If the group was created without a display name',
      paragraphs: ['Only needed after the “DisplayName can’t be empty” error:'],
      code: [`Set-RoleGroup -Identity "${PURVIEW_GROUP}" -DisplayName "${PURVIEW_GROUP}"`],
    },
    {
      id: 'purview-add-member',
      heading: 'Add DriftWatch to the role group',
      paragraphs: ['A successful run prints nothing: the prompt simply returns. Any red text is an error.'],
      code: [`Add-RoleGroupMember -Identity "${PURVIEW_GROUP}" -Member $SpObjectId`],
      bullets: [
        '“DisplayName can’t be empty” — run the Set-RoleGroup command above, then this one again.',
        `“'${PURVIEW_GROUP}' couldn’t be found” — the group does not exist in this session: creating it failed, the name differs, or the window is connected to Exchange.`,
      ],
    },
    {
      id: 'purview-verify',
      heading: 'Check the result',
      paragraphs: ['Two checks. The first must list DriftWatch and nobody else; the second the group and its three roles.'],
      code: [
        `Get-RoleGroupMember -Identity "${PURVIEW_GROUP}"`,
        `Get-RoleGroup -Identity "${PURVIEW_GROUP}" | Format-List Name, DisplayName, Roles`,
      ],
      bullets: [
        'The member is listed as DriftWatch (read-only) with RecipientType MailUser. MailUser is how Security & Compliance lists an application; it does not mean DriftWatch has a mailbox.',
        `Name and DisplayName both read ${PURVIEW_GROUP}. An empty DisplayName means adding the member will fail.`,
        'Roles lists View-Only DLP Compliance Management, Sensitivity Label Reader and View-Only Retention Management, each as a long path ending in the role’s name, and nothing else.',
        'Get-ManagementRole shows RoleEntries as empty for every Purview role, and Get-ManagementRoleEntry does not exist in this session. Neither is a fault. It is why DriftWatch judges the session by the commands it is actually given rather than by role names.',
      ],
    },
    {
      id: 'purview-disconnect',
      heading: 'Disconnect and assess again',
      paragraphs: [
        'Disconnect rather than only closing the window. Nothing needs triggering in DriftWatch: the next assessment, scheduled or started by hand, picks the membership up. It applied within minutes in our testing; Microsoft says it can take up to an hour. If DP-024 to DP-026 still say not permitted after an hour, look up the code the finding shows under Troubleshooting below. Do not add roles in the meantime.',
      ],
      code: ['Disconnect-ExchangeOnline -Confirm:$false'],
    },
    {
      id: 'purview-beyond-reading',
      heading: 'What these roles allow beyond reading',
      paragraphs: [
        'Microsoft’s view-only Purview roles are not entirely read-only. Together, the three give the application six commands that are not reads. No narrower Microsoft role reads data loss prevention policies, so the choice is between these three roles and not assessing Purview at all (ADR-0071).',
        'DriftWatch allows exactly these six and never runs any of them: it runs only a fixed list of read commands, and checks every script against that list first. Every assessment also lists the commands the session offers, and discards everything read from Purview if any other command that is not a read appears. Someone who stole DriftWatch’s certificate could use the first three in any tenant that has granted this role group.',
      ],
      bullets: [
        'Set-DlpEdmSchema — changes an exact data match schema, a genuine change to the tenant.',
        'Cancel-DlpEdmSession — cancels an exact data match upload in progress.',
        'Execute-AzureAdLabelSync — starts a synchronisation of sensitivity labels to Entra ID.',
        'Check-PurviewConfig — checks Purview configuration.',
        'Export-PurviewConfig — exports Purview configuration.',
        'Validate-RetentionRuleQuery — validates a retention rule’s query.',
      ],
    },
    {
      id: 'purview-licences',
      heading: 'Licences for these three controls',
      paragraphs: [
        'The role group lets DriftWatch read; the licence decides whether there is anything to read. DriftWatch checks the licence first, and a tenant without it is reported as a licence gap — Licence capability required — whatever roles it holds.',
      ],
      bullets: [
        'DP-024 — Microsoft Purview Data Loss Prevention: Exchange Online (Plan 2), SharePoint Online (Plan 2), or the Data Loss Prevention plan in Microsoft 365 Business Premium.',
        'DP-025 — Microsoft Communications DLP, in the E5, A5 and G5 suites and Microsoft 365 E5 Compliance. Not in E3 or Business Premium.',
        'DP-026 — Microsoft Purview Information Protection for Office 365, or Azure Information Protection Premium P1 (included in Business Premium).',
      ],
    },
    {
      id: 'purview-troubleshoot',
      heading: 'Troubleshooting: the code on the finding',
      paragraphs: [
        'Each Purview finding names the sources it could not read, each with a code in brackets, such as dlpPolicies: unauthorized (ComplianceRoleMissing). The code says who has to act.',
      ],
      bullets: [
        'ComplianceRoleMissing — DriftWatch is not in a role group that includes the read. Run the membership check above; if it is in place, wait up to an hour.',
        'DirectoryRoleNotPermitted — DriftWatch holds an Entra directory role other than Teams Reader, active or eligible, directly or through a group. Remove it in the Entra admin centre under Roles and administrators. DriftWatch then reads neither Purview nor Exchange until it is gone.',
        'DirectoryRoleUnverified — a Graph read the directory-role check depends on failed. Usually consent is incomplete: grant it again.',
        'ComplianceSessionUnconstrained — the session offered a command that can change the tenant beyond the six above, usually because DriftWatch was added to a broader role group. Find and remove it with the commands below.',
        'UnconstrainedPrincipal — the Exchange role check failed, and Purview is discarded with Exchange. Fix the Exchange roles first.',
        'CredentialRejected — Microsoft refused DriftWatch’s own sign-in. Change nothing in the client tenant; the provider renews the credential.',
        'PwshSessionFailed, PwshTimeout or PwshNoOutput — the session did not connect or answer in time. Run the assessment again; if it recurs, tell your provider.',
      ],
    },
    {
      id: 'purview-broader-group',
      heading: 'Removing DriftWatch from a broader role group',
      paragraphs: [
        'For ComplianceSessionUnconstrained. Check the three broad groups; a row named DriftWatch (read-only), or whatever display name it was registered with, means it is a member. Remove it from every group except its own, replacing <ROLE GROUP NAME> and answering Y:',
      ],
      code: [
        'Get-RoleGroupMember -Identity "Compliance Administrator"',
        'Get-RoleGroupMember -Identity "Compliance Data Administrator"',
        'Get-RoleGroupMember -Identity "Organization Management"',
        'Remove-RoleGroupMember -Identity "<ROLE GROUP NAME>" -Member $SpObjectId',
      ],
      bullets: [
        `Then run the Get-RoleGroup check above. If ${PURVIEW_GROUP} holds a fourth role, remove DriftWatch from it, delete the group, and create it again exactly as above.`,
        'If nothing turns up and the named commands are unfamiliar, Microsoft may have added one to a view-only role. Change nothing; tell your provider, who will review it.',
      ],
    },
    {
      id: 'purview-do-not',
      heading: 'What not to do',
      paragraphs: ['Each of these makes DriftWatch refuse to read, rather than read more.'],
      bullets: [
        'Do not add DriftWatch to Compliance Administrator, Compliance Data Administrator or Organization Management. They can change data loss prevention and labelling policies.',
        'Do not give it an Entra directory role other than Teams Reader — Global Reader, Security Reader and Compliance Administrator included.',
        'Do not add View-Only Audit Logs to the role group. In Purview it opens audit-log search, which DriftWatch does not use. It is a different thing from the Exchange role of the same name, which DriftWatch does need.',
        'Do not run these commands in an Exchange Online window.',
        'Do not run any of this in the tenant that hosts DriftWatch. The role group belongs to each client tenant separately.',
      ],
    },
    {
      id: 'purview-remove',
      heading: 'Removing access',
      paragraphs: [
        'In a Security & Compliance window, with $SpObjectId set as above. Answer Y when asked, or add -Confirm:$false. The membership check should then print nothing, and the empty group can be removed.',
      ],
      code: [
        `Remove-RoleGroupMember -Identity "${PURVIEW_GROUP}" -Member $SpObjectId`,
        `Get-RoleGroupMember -Identity "${PURVIEW_GROUP}"`,
        `Remove-RoleGroup -Identity "${PURVIEW_GROUP}"`,
      ],
      bullets: [
        'This withdraws Purview access alone. Consent and the Exchange roles are not affected.',
        'DP-024 to DP-026 then report as Not evidenced again, which is the correct result.',
      ],
    },
  ],
};
